<template>
  <div class="about-page">
    <!-- Background Image -->
    <div class="about-banner-bg"></div>
    
    <!-- Intro Section -->
    <AboutIntroComp :content="introContent" />

    <!-- Mission & Vision Section -->
    <AboutMissionVisionComp :content="missionVisionContent" />

    <!-- Team Snapshot Section -->
    <AboutTeamComp :content="teamContent" />

    <!-- Our Journey Section -->
    <AboutJourneyComp :content="journeyContent" />

    <!-- Clients & Partners Section -->
    <AboutClientsComp :content="clientsContent" />

    <!-- Call to Action Section -->
    <AboutCtaComp :content="ctaContent" />
    </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import AboutIntroComp from '@/components/AboutIntroComp.vue'
import AboutMissionVisionComp from '@/components/AboutMissionVisionComp.vue'
import AboutTeamComp from '@/components/AboutTeamComp.vue'
import AboutJourneyComp from '@/components/AboutJourneyComp.vue'
import AboutClientsComp from '@/components/AboutClientsComp.vue'
import AboutCtaComp from '@/components/AboutCtaComp.vue'

export default {
  name: 'AboutPage',
  components: {
    AboutIntroComp,
    AboutMissionVisionComp,
    AboutTeamComp,
    AboutJourneyComp,
    AboutClientsComp,
    AboutCtaComp
  },
  setup() {
    const introContent = ref({
      sectionLabel: 'About Sogdo',
      mainTitle: 'Who We Are',
      description: 'We are a forward-thinking technology company dedicated to transforming ideas into innovative solutions. Our team of experts combines deep technical knowledge with creative problem-solving to deliver exceptional results for our clients worldwide.'
    })

    const missionVisionContent = ref({
      header: {
        sectionLabel: 'Our Foundation',
        mainTitle: 'Mission & Vision',
        description: 'Our mission and vision drive everything we do, guiding us toward a future where technology serves humanity and creates meaningful impact.'
      },
      mission: {
        icon: 'icon-01',
        title: 'Our Mission',
        description: 'To build meaningful technology for real-world challenges. We believe in the transformative power of technology to solve complex challenges and create opportunities for success.',
        features: [
          'Innovation First',
          'Client Success',
          'Quality Excellence',
          'Continuous Learning'
        ]
      },
      vision: {
        icon: 'icon-02',
        title: 'Our Vision',
        description: 'To be the most trusted software innovation partner. We envision a world where technology seamlessly integrates with human potential, creating sustainable solutions that benefit society and drive global progress.',
        features: [
          'Global Impact',
          'Sustainable Solutions',
          'Human-Centered Design',
          'Future-Ready'
        ]
      }
    })

    const teamContent = ref({
      header: {
        sectionLabel: 'Our People',
        mainTitle: 'Team Snapshot',
        description: 'Meet the talented individuals who make Sogdo a success. Our diverse team brings together expertise from around the world to deliver exceptional results.'
      },
      teamMembers: [
        {
          name: 'Sarah Johnson',
          role: 'CEO & Founder',
          description: 'Visionary leader with 15+ years in technology innovation and business strategy.',
          icon: 'icon-01',
          expertise: ['Strategy', 'Leadership']
        },
        {
          name: 'Michael Chen',
          role: 'CTO',
          description: 'Technology architect with expertise in AI/ML, cloud computing, and scalable systems.',
          icon: 'icon-02',
          expertise: ['AI/ML', 'Cloud']
        },
        {
          name: 'Emily Rodriguez',
          role: 'Lead Designer',
          description: 'Creative director specializing in user experience design and digital innovation.',
          icon: 'icon-03',
          expertise: ['UX/UI', 'Design']
        },
        {
          name: 'David Kim',
          role: 'Lead Developer',
          description: 'Full-stack developer with expertise in modern web technologies and mobile applications.',
          icon: 'icon-04',
          expertise: ['Full-Stack', 'Mobile']
        }
      ]
    })

    const journeyContent = ref({
      header: {
        sectionLabel: 'Our Story',
        mainTitle: 'Our Journey',
        description: 'From humble beginnings to becoming a leading technology company, our journey has been marked by innovation, growth, and unwavering commitment to excellence.'
      },
      timeline: [
        {
          year: '2018',
          title: 'Startup to Today',
          description: 'Sogdo was founded with a vision to revolutionize technology solutions and create meaningful impact through innovation.'
        },
        {
          year: '2019',
          title: 'Projects Completed',
          description: 'Secured our first enterprise client, marking the beginning of our journey in delivering large-scale technology solutions.'
        },
        {
          year: '2020',
          title: 'Clients Served',
          description: 'Launched our AI/ML research division, positioning ourselves at the forefront of artificial intelligence innovation.'
        },
        {
          year: '2021',
          title: 'Team: Founders, Core Leadership',
          description: 'Expanded operations internationally, establishing partnerships and serving clients across multiple continents.'
        },
        {
          year: '2022',
          title: 'Stats: Years Active',
          description: 'Pioneered blockchain solutions and smart contract development, becoming a leader in decentralized technology.'
        },
        {
          year: '2023',
          title: 'Stats: Projects Completed',
          description: 'Achieved recognition for our cloud architecture solutions and microservices expertise in enterprise environments.'
        },
        {
          year: '2024',
          title: 'Stats: Clients Served',
          description: 'Continuing to innovate with emerging technologies, quantum computing research, and next-generation solutions.'
        }
      ]
    })

    const clientsContent = ref({
      header: {
        sectionLabel: 'Trusted By',
        mainTitle: 'Clients & Partners',
        description: 'We\'re proud to work with industry leaders and innovative companies that share our vision of technological excellence and growth.'
      },
      clients: [
        {
          name: 'TechCorp',
          description: 'Enterprise Solutions',
          logoText: 'TechCorp'
        },
        {
          name: 'InnovateLab',
          description: 'AI/ML Research',
          logoText: 'InnovateLab'
        },
        {
          name: 'CloudTech',
          description: 'Cloud Infrastructure',
          logoText: 'CloudTech'
        },
        {
          name: 'DataFlow',
          description: 'Data Analytics',
          logoText: 'DataFlow'
        },
        {
          name: 'BlockChain Pro',
          description: 'Blockchain Solutions',
          logoText: 'BlockChain Pro'
        },
        {
          name: 'IoT Solutions',
          description: 'Smart Devices',
          logoText: 'IoT Solutions'
        }
      ]
    })

    const ctaContent = ref({
      title: 'Ready to Work With Us?',
      description: 'Join our journey of innovation and excellence. Let\'s create something amazing together and shape the future of technology.',
      buttons: [
        {
          text: 'Get In Touch',
          link: '/contact',
          icon: 'icon-02',
          type: 'primary-btn'
        },
        {
          text: 'Join Our Team',
          link: '/careers',
          icon: 'icon-02',
          type: 'secondary-btn'
        }
      ]
    })

    onMounted(() => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()
    })

    return {
      introContent,
      missionVisionContent,
      teamContent,
      journeyContent,
      clientsContent,
      ctaContent
    }
  }
}
</script>

<style scoped>
/* About Page Container */
.about-page {
  position: relative;
  z-index: 1;
}

/* About Banner Background */
.about-banner-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/images/banner/banner1.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  z-index: -1;
  filter: brightness(0.3);
  pointer-events: none;
}
</style> 